import React, {useEffect, useMemo, useState} from "react";
import {handleGraphQLError} from "../utils/graphqlErrorHandler";
import Slide from "@mui/material/Slide";
import useTriStateSort from "../hooks/useTriStateSort";
import PageHeader from "../components/PageHeader";
import StoreIcon from "@mui/icons-material/Store";
import {formatToLocalTime} from "../utils/dateUtils";
import SearchField from "../components/SearchField";
import {Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Paper, Snackbar, Stack, Table, TableBody, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField, Toolbar, Tooltip, Typography, useMediaQuery} from "@mui/material";
import {gql, useMutation, useQuery} from "@apollo/client";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import {useTranslation} from "react-i18next";
import EllipsisCell from "../components/EllipsisCell";
import TablePaginationActions from "../components/TablePaginationActions";

const SlideUp = (props) => <Slide {...props} direction="up"/>;

const PAGINATE_RESELLERS = gql`
    query PaginateReseller(
        $pagination: Pagination!
        $sortBy: [SortBy!]
        $criteria: ResellerCriteria
    ) {
        paginateReseller(
            pagination: $pagination
            sortBy: $sortBy
            criteria: $criteria
        ) {
            pageInfo {
                totalElements
                totalPages
                numberOfElements
                pageNumber
                pageSize
            }
            content {
                id
                name
                deleted
                updatedTimestamp
            }
        }
    }
`;

const CREATE_RESELLER = gql`
    mutation CreateReseller($input: ResellerInput!) {
        createReseller(input: $input) {
            id
            name
        }
    }
`;

const UPDATE_RESELLER = gql`
    mutation UpdateReseller($id: ID!, $input: ResellerInput!) {
        updateReseller(id: $id, input: $input) {
            id
            name
        }
    }
`;

const DELETE_RESELLER = gql`
    mutation DeleteReseller($id: ID!) {
        deleteReseller(id: $id)
    }
`;

const Resellers = () => {
    const {t} = useTranslation();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRpp] = useState(10);

    const {sortFields, handleSort, getSortProps} = useTriStateSort(
        (updated) => {
            refetch({...variables, sortBy: updated});
        }
    );

    const [nameFilter, setNameFilter] = useState("");
    const [debouncedFilters, setDebouncedFilters] = useState({name: ""});


    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);


    const [createForm, setCreateForm] = useState({name: ""});
    const [editForm, setEditForm] = useState({id: "", name: ""});
    const [deleteId, setDeleteId] = useState("");


    const [snackbar, setSnackbar] = useState({open: false, message: "", severity: "success"});
    const [createError, setCreateError] = useState("");
    const [createApiError, setCreateApiError] = useState({reseller: "", general: ""});
    const [editApiError, setEditApiError] = useState({reseller: "", general: ""});
    const [editError, setEditError] = useState("");
    const [deleteError, setDeleteError] = useState("");
    const isSmall = useMediaQuery("(max-width:600px)");

    const variables = useMemo(
        () => ({
            pagination: {page, itemsPerPage: rowsPerPage},
            sortBy: sortFields,
            criteria: {
                deleted: false,
                ...(debouncedFilters.name ? {name: debouncedFilters.name} : {}),
            },
        }),
        [page, rowsPerPage, sortFields, debouncedFilters]
    );

    const {data, loading, error, refetch} = useQuery(PAGINATE_RESELLERS, {
        variables,
        fetchPolicy: "no-cache",
    });

    const [createReseller, {loading: creating}] = useMutation(CREATE_RESELLER);
    const [updateReseller, {loading: updating}] = useMutation(UPDATE_RESELLER);
    const [deleteReseller, {loading: deleting}] = useMutation(DELETE_RESELLER);

    const pageInfo = data?.paginateReseller?.pageInfo;
    const rows = data?.paginateReseller?.content ?? [];

    const handleChangePage = (_e, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRpp(parseInt(e.target.value, 10));
        setPage(0);
    };


    const handleSearchKeyDown = (e) => {
        if (e.key === "Enter") {
            setDebouncedFilters({name: nameFilter.trim()});
            setPage(0);
        }
    };

    const applyFilters = () => {
        setDebouncedFilters({
            name: nameFilter.trim(),
        });
        setPage(0);
    };

    const openEdit = (row) => {
        setEditForm({id: row.id, name: row.name});
        setEditOpen(true);
    };

    const openDelete = (row) => {
        setDeleteId(row.id);
        setEditForm({name: row.name});
        setDeleteOpen(true);
    };

    const handleCloseCreate = () => {
        setCreateOpen(false);
        setCreateError("");
        setCreateApiError({reseller: "", general: ""});
        setCreateForm({name: ""});
    };

    const handleCloseEdit = () => {
        setEditOpen(false);
        setEditApiError({reseller: "", general: ""});
        setEditError("");
    };

    const handleCloseDelete = () => {
        setDeleteOpen(false);
        setDeleteError("");
    };

    const doCreate = async () => {
        setCreateApiError({reseller: "", general: ""});
        if (!createForm.name.trim()) {
            setCreateError(t("validation.requiredAll"));
            return;
        }
        try {
            setCreateError("");
            await createReseller({variables: {input: createForm}});
            handleCloseCreate();
            setSnackbar({open: true, message: t("resellerPage.messages.created"), severity: "success"});
            refetch();
        } catch (error) {
            handleGraphQLError(error, setCreateApiError, t);
        }
    };

    const doUpdate = async () => {
        setEditApiError({reseller: "", general: ""});
        if (!editForm.name.trim()) {
            setEditError(t("validation.requiredAll"));
            return;
        }
        try {
            await updateReseller({variables: {id: editForm.id, input: {name: editForm.name}}});
            handleCloseEdit();
            setSnackbar({open: true, message: t("resellerPage.messages.updated"), severity: "success"});
            refetch();
        } catch (error) {
            handleGraphQLError(error, setEditApiError, t);
        }
    };

    const doDelete = async () => {
        try {
            await deleteReseller({variables: {id: deleteId}});
            handleCloseDelete();
            setSnackbar({open: true, message: t("resellerPage.messages.deleted"), severity: "success"});
            refetch();
        } catch (error) {
            const msg = error?.graphQLErrors?.[0]?.message || t("common.deleteFailed");
            setDeleteError(msg);
        }
    };

    useEffect(() => {
        if (!createOpen) setCreateError("");
    }, [createOpen]);
    useEffect(() => {
        if (!editOpen) setEditError("");
    }, [editOpen]);
    useEffect(() => {
        if (!deleteOpen) setDeleteError("");
    }, [deleteOpen]);

    const onResetFilter = () => {
        setNameFilter("");
        setDebouncedFilters({name: ""});
        setPage(0);
    };

    const onRefresh = () => refetch();

    return (
        <Box>
            {/* <Typography variant="h5" gutterBottom>{t("resellerPage.title") || "Reseller Management"}</Typography> */}
            <PageHeader
                title={t("resellerPage.title")}
                subtitle={t("resellerPage.subtitle")}
                icon={<StoreIcon/>}
            />

            <Paper elevation={3} sx={{borderRadius: 2, overflow: "hidden"}}>
                <Toolbar
                    sx={{
                        px: 2,
                        py: 1,
                        gap: 1,
                        flexWrap: "wrap",
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                        bgcolor: "background.paper",
                    }}
                >

                    <SearchField
                        label={t("resellerPage.searchNamePlaceholder")}
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        placeholder={t("resellerPage.searchNamePlaceholder")}
                        isSmall={isSmall}
                    />

                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SearchIcon/>}
                        onClick={applyFilters}
                        sx={{
                            height: 40,
                            px: 2,
                            textTransform: "none",
                            fontWeight: 500,
                            minWidth: isSmall ? "100%" : "auto",
                        }}
                    >
                        {t("common.search")}
                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<ClearIcon/>}
                        onClick={onResetFilter}
                        sx={{
                            height: 40,
                            px: 2,
                            textTransform: "none",
                            fontWeight: 500,
                            minWidth: isSmall ? "100%" : "auto",
                        }}
                    >
                        {t("common.clear")}
                    </Button>

                    <Box sx={{flex: 1}}/>


                    <Tooltip title={t("common.refresh")}>
            <span>
              <IconButton onClick={onRefresh} aria-label="refresh">
                <RefreshIcon/>
              </IconButton>
            </span>
                    </Tooltip>

                    <Button variant="contained" startIcon={<AddIcon/>} onClick={() => setCreateOpen(true)}>
                        {t("resellerPage.createButton")}
                    </Button>
                </Toolbar>

                {loading ? (
                    <Box sx={{p: 4, textAlign: "center"}}>
                        <CircularProgress/>
                    </Box>
                ) : error ? (
                    <Box sx={{p: 2}}>
                        <Alert severity="error">{error.message}</Alert>
                    </Box>
                ) : (
                    <>
                        <TableContainer sx={{maxHeight: 520}}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <EllipsisCell sx={{
                                            bgcolor: "grey.50",
                                            fontWeight: 600,
                                            fontSize: "0.875rem",
                                            borderBottom: "2px solid",
                                            borderBottomColor: "divider",
                                            color: "text.primary"
                                        }}>
                                            <TableSortLabel
                                                {...getSortProps("name")}
                                                onClick={() => handleSort("name")}
                                            >
                                                {t("resellerPage.table.name")}
                                            </TableSortLabel>
                                        </EllipsisCell>
                                        <EllipsisCell sx={{
                                            bgcolor: "grey.50",
                                            fontWeight: 600,
                                            fontSize: "0.875rem",
                                            borderBottom: "2px solid",
                                            borderBottomColor: "divider",
                                            color: "text.primary",
                                            width: "1%",
                                            whiteSpace: "nowrap"
                                        }}>
                                            <TableSortLabel
                                                {...getSortProps("updatedTimestamp")}
                                                onClick={() => handleSort("updatedTimestamp")}
                                            >
                                                {t("userPage.table.updated")}
                                            </TableSortLabel>
                                        </EllipsisCell>
                                        <EllipsisCell sx={{
                                            bgcolor: "grey.50",
                                            fontWeight: 600,
                                            fontSize: "0.875rem",
                                            borderBottom: "2px solid",
                                            borderBottomColor: "divider",
                                            color: "text.primary",
                                            width: "1%",
                                            whiteSpace: "nowrap"
                                        }}>
                                            {t("userPage.table.actions")}
                                        </EllipsisCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.length === 0 ? (
                                        <TableRow>
                                            <EllipsisCell colSpan={3} align="center">{t("userPage.noRecords")}</EllipsisCell>
                                        </TableRow>
                                    ) : (
                                        rows.map((r) => (
                                            <TableRow
                                                key={r.id}
                                                hover
                                                sx={{
                                                    "&:nth-of-type(even)": {
                                                        bgcolor: "grey.50"
                                                    },
                                                    "&:hover": {
                                                        bgcolor: "action.hover"
                                                    }
                                                }}
                                            >
                                                <EllipsisCell>
                                                    {r.name}
                                                </EllipsisCell>
                                                <EllipsisCell sx={{whiteSpace: "nowrap"}}>
                                                    {r.updatedTimestamp ? formatToLocalTime(r.updatedTimestamp) : "-"}
                                                </EllipsisCell>
                                                <EllipsisCell sx={{whiteSpace: "nowrap"}}>
                                                    <Tooltip title={t("common.update")}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => openEdit(r)}
                                                        >
                                                            <EditIcon/>
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={t("common.delete")}>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => openDelete(r)}
                                                        >
                                                            <DeleteIcon/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </EllipsisCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            labelRowsPerPage={t("common.rowsPerPage")}
                            component="div"
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            count={pageInfo?.totalElements ?? 0}
                            rowsPerPage={pageInfo?.pageSize ?? rowsPerPage}
                            page={pageInfo?.pageNumber ?? page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                        />
                    </>
                )}
            </Paper>


            <Dialog open={createOpen} onClose={handleCloseCreate} fullWidth maxWidth="sm">
                <DialogTitle sx={{fontWeight: "bold"}}>{t("resellerPage.createTitle")}</DialogTitle>
                <Divider sx={{mb: 1, borderColor: "divider", opacity: 0.7}}/>
                {createApiError.general && <Alert severity="error" sx={{mb: 2}}>{createApiError.general}</Alert>}
                <DialogContent sx={{pt: 1}}>
                    <Stack spacing={2} sx={{mt: 1}}>
                        <TextField
                            label={t("resellerPage.form.nameLabel")}
                            value={createForm.name}
                            onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                            error={createApiError.reseller || (createError && !createForm.name.trim())}
                            helperText={
                                createApiError.reseller ||
                                (createError && !createForm.name.trim() ? t("validation.requiredField") : "")
                            }
                            autoFocus
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCreate}>{t("common.cancel")}</Button>
                    <Button onClick={doCreate} disabled={creating} aria-busy={creating} variant="contained">
                        {creating ? <CircularProgress size={20}/> : t("common.create")}
                    </Button>
                </DialogActions>
            </Dialog>


            <Dialog open={editOpen} onClose={handleCloseEdit} fullWidth maxWidth="sm">
                <DialogTitle sx={{fontWeight: "bold"}}>{t("resellerPage.updateTitle")}</DialogTitle>
                <Divider sx={{mb: 1, borderColor: "divider", opacity: 0.7}}/>
                {editApiError.general && <Alert severity="error" sx={{mb: 2}}>{editApiError.general}</Alert>}
                <DialogContent sx={{pt: 1}}>
                    <Stack spacing={2} sx={{mt: 1}}>
                        <TextField
                            label={t("resellerPage.form.nameLabel")}
                            value={editForm.name}
                            onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                            error={editApiError.reseller || (editError && !editForm.name.trim())}
                            helperText={
                                editApiError.reseller ||
                                (editError && !editForm.name.trim() ? t("validation.requiredField") : "")
                            }
                            autoFocus
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEdit}>{t("common.cancel")}</Button>
                    <Button onClick={doUpdate} disabled={updating} aria-busy={updating} variant="contained">
                        {updating ? <CircularProgress size={20}/> : t("common.update")}
                    </Button>
                </DialogActions>
            </Dialog>


            <Dialog open={deleteOpen} onClose={handleCloseDelete}>
                <DialogTitle sx={{fontWeight: "bold"}}>{t("resellerPage.deleteConfirmTitle")}</DialogTitle>
                <Divider sx={{mb: 1, borderColor: "divider", opacity: 0.7}}/>
                {deleteError && <Alert severity="error" sx={{mb: 2}}>{deleteError}</Alert>}
                <DialogContent>
                    <Typography variant="h6" sx={{color: "#d32f2f", fontWeight: "bold", mt: 1}}>
                        {editForm.name}
                    </Typography>
                    <Typography>{t("resellerPage.deleteConfirmMessage")}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDelete}>{t("common.cancel")}</Button>
                    <Button onClick={doDelete} color="error" disabled={deleting} aria-busy={deleting} variant="contained">
                        {deleting ? <CircularProgress size={20}/> : t("common.delete")}
                    </Button>
                </DialogActions>
            </Dialog>


            <Snackbar
                TransitionComponent={SlideUp}
                open={snackbar.open}
                autoHideDuration={2500}
                onClose={() => setSnackbar({...snackbar, open: false})}
                anchorOrigin={{vertical: "bottom", horizontal: "center"}}
                sx={{
                    "& .MuiAlert-root": {
                        borderRadius: 2,
                        boxShadow: "0px 4px 12px rgba(0,0,0,0.25)",
                        fontWeight: 500,
                        minWidth: 260,
                        justifyContent: "center",
                    },
                }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({...snackbar, open: false})}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Resellers;
